/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import type { ShouldRevalidateFunctionArgs } from 'react-router';
import { getSearchWithoutClientOnlyParams } from '@/hooks/use-filters-panel-state';
// @sfdc-extension-line SFDC_EXT_BOPIS
import { resourceRoutes } from '@/route-paths';
import { getActionPath, isAmbientMutation } from './shared';

/**
 * Shared `shouldRevalidate` policy for the product listing pages (category + search). Both routes derive their data
 * purely from the URL (`q` / `refine` / `sort` / `offset` / category id) and the site context (currency), so the
 * loader only needs to re-run when one of those actually changes.
 *
 * Two cases are suppressed beyond React Router's default:
 *
 * 1. **Client-only param toggles.** The filters-panel open/closed flag and pending-action params live in the URL for
 *    shareability but never affect server data. A navigation that touches only those, is skipped.
 * 2. **Listing-irrelevant mutations.** A non-`GET` submission triggers RR's default post-action revalidation, but
 *    cart/wishlist/etc. mutations don't change listing data — cart state propagates via the `__sfdc_basket` cookie
 *    and the action's own fetcher result, wishlist via the client-side provider. Only the shared
 *    {@link isAmbientMutation} dimensions are allowed to revalidate: currency / shopper-context change the
 *    loader's SCAPI prices and promotions, and an auth identity transition (login / signup / logout) re-scopes the
 *    auth-dependent `wishlistInitialState` seed the loader feeds the `WishlistProvider`. A submission with no
 *    resolvable `formAction` cannot be confirmed as an ambient dimension, so it is skipped too.
 *
 * `setSelectedStore` IS admitted, and the paragraph that used to sit here said it was not. That was correct while the
 * listing carried nothing store-scoped, and it stopped being correct on 2026-08-23 when the rail's store-availability
 * filter started refining on `c_availableStores` (see `STORE_AVAILABILITY_REFINEMENT`). The mechanism is exactly the
 * one this policy's own note predicted: picking a store from the rail submits `set-selected-store` as a POST *and*
 * pushes `refine=c_availableStores=<storeId>` into the URL in the same navigation. Suppressing the revalidation left
 * the URL, the applied-filter chip and the checkbox all showing the filter as ON while the grid still held the
 * unfiltered results — measured on NM `/search?q=dress`: URL and chip updated, tiles stayed at 23 where a fresh load
 * of the identical URL returns 12.
 *
 * `cartPickupStoreUpdate` is still NOT admitted: it mutates basket line items and shipments, none of which the
 * listing loader reads.
 *
 * Everything else falls through to `defaultShouldRevalidate`, so genuine navigations (new query, refinement, sort,
 * page) and explicit `useRevalidator()` calls (no `formMethod`) behave normally.
 */
export function shouldRevalidate({
    currentUrl,
    nextUrl,
    formMethod,
    formAction,
    defaultShouldRevalidate,
}: ShouldRevalidateFunctionArgs): boolean {
    // Client-only param toggles (filters panel, pending action)
    const clientOnlyParamsChanged =
        currentUrl.pathname === nextUrl.pathname &&
        currentUrl.search !== nextUrl.search &&
        getSearchWithoutClientOnlyParams(currentUrl.search) === getSearchWithoutClientOnlyParams(nextUrl.search);
    if (clientOnlyParamsChanged) {
        return false;
    }

    // A mutation submitted from the listing (formMethod is set and non-GET). Skip the revalidation unless the action
    // is one of the ambient dimensions that actually changes listing data. An unclassifiable submission (no
    // `formAction`) cannot be confirmed as an ambient dimension, so it is skipped too.
    if (formMethod && formMethod !== 'GET') {
        const actionPath = getActionPath(formAction, currentUrl.origin);
        // @sfdc-extension-line SFDC_EXT_BOPIS
        const isStoreSelection = actionPath === resourceRoutes.setSelectedStore;
        if (!actionPath || !(isAmbientMutation(actionPath) || isStoreSelection)) {
            return false;
        }
    }

    return defaultShouldRevalidate;
}
